import {useEffect, type RefObject} from "react";

/**
 * Отклик на нажатие для всей страницы одним обработчиком.
 *
 * Почему не CSS `:active`, который тут уже был. На iOS псевдокласс `:active` для
 * обычных кнопок не срабатывает вовсе, пока на элементе (или на документе) нет
 * обработчика касания, — это давняя особенность Safari. Именно поэтому кнопки
 * лендинга «не отзывались» на телефоне: правило в стилях было, но браузер его
 * не применял. Указательные события (Pointer Events) работают одинаково и на
 * пальце, и на мыши, и на стилусе.
 *
 * Что делает (скилл apple-design):
 *   • §1 «respond on pointer-down» — метка `data-pressed` ставится в момент
 *     касания, а не на отпускание. Ждать отпускания — значит показать отклик
 *     после того, как всё уже произошло.
 *   • §10 «cancel-by-dragging-away and back» — если палец уехал с кнопки,
 *     нажатие снимается; вернулся в её пределы — возвращается. Плюс запас 10 px
 *     вокруг кнопки, чтобы дрожь пальца не гасила нажатие.
 *   • §3 «never lock out input» — обработчики пассивные, прокрутку не блокируют.
 *     Когда браузер забирает жест под прокрутку, приходит `pointercancel`, и
 *     нажатие снимается само: страница едет — кнопка не залипла нажатой.
 *
 * Один делегированный обработчик, а не проп на каждой кнопке: на странице
 * четыре десятка нажимаемых элементов в семи разных компонентах, и любой новый
 * без единой точки отклик бы потерял.
 */

/**
 * Что считается нажимаемым. Только настоящие действия: кнопки, ссылки
 * навигации и подвала, логотип. Карточки разделов сюда не входят намеренно —
 * отклик на нажатие у того, что нажимать некуда, обещает переход, которого не
 * будет (скилл §16.4 «familiarity», §16 «grouping & mapping»).
 */
const PRESSABLE = [
    ".rl-btn",
    ".rl-nav-links a",
    ".rl-f-cols a",
    ".rl-f-cols button",
    ".rl-brandmark",
    "[data-pressable]",
].join(",");

/** Запас вокруг кнопки, в пределах которого палец ещё считается «на ней». */
const SLOP = 10;

export function usePressFeedback(rootRef: RefObject<HTMLElement | null>): void {
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        /** Элемент, на котором начали нажатие. Вернуться можно только к нему. */
        let origin: HTMLElement | null = null;
        let activePointer: number | null = null;

        const mark = (on: boolean) => {
            if (!origin) return;
            if (on) origin.setAttribute("data-pressed", "");
            else origin.removeAttribute("data-pressed");
        };

        const release = () => {
            mark(false);
            origin = null;
            activePointer = null;
        };

        const onPointerDown = (event: PointerEvent) => {
            // Только основная кнопка мыши: правый клик открывает меню, а не жмёт.
            if (event.button !== 0) return;
            const target = (event.target as Element | null)?.closest<HTMLElement>(PRESSABLE);
            if (!target || !root.contains(target)) return;
            release();
            origin = target;
            activePointer = event.pointerId;
            mark(true);
        };

        const onPointerMove = (event: PointerEvent) => {
            if (activePointer !== event.pointerId || !origin) return;
            const rect = origin.getBoundingClientRect();
            const inside =
                event.clientX >= rect.left - SLOP &&
                event.clientX <= rect.right + SLOP &&
                event.clientY >= rect.top - SLOP &&
                event.clientY <= rect.bottom + SLOP;
            // Уехали — гасим, вернулись — зажигаем снова. Обещание нажатия должно
            // соответствовать тому, что произойдёт на отпускании.
            mark(inside);
        };

        root.addEventListener("pointerdown", onPointerDown, {passive: true});
        root.addEventListener("pointermove", onPointerMove, {passive: true});
        // Отпускание и отмену слушаем на документе: палец мог уехать за пределы
        // страницы, и тогда события до корня лендинга уже не дойдут.
        document.addEventListener("pointerup", release, {passive: true});
        document.addEventListener("pointercancel", release, {passive: true});
        window.addEventListener("blur", release);

        return () => {
            root.removeEventListener("pointerdown", onPointerDown);
            root.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerup", release);
            document.removeEventListener("pointercancel", release);
            window.removeEventListener("blur", release);
            release();
        };
    }, [rootRef]);
}
